import { useCallback, useEffect, useRef, useState } from 'react';
import useAlbumData from './useAlbumData';

interface UseIndianMusicInfiniteScrollOptions {
  rootMargin?: string;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  enabled?: boolean;
  genre?: string;
  search?: string;
}

interface UseIndianMusicInfiniteScrollReturn {
  loading: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  reset: () => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  albums: any[];
}

export const useIndianMusicInfiniteScroll = (
  options: UseIndianMusicInfiniteScrollOptions = {},
): UseIndianMusicInfiniteScrollReturn => {
  const {
    rootMargin = '200px',
    scrollContainerRef,
    enabled = true,
    genre,
    search,
  } = options;

  const [albums, setAlbums] = useState<any[]>([]);
  const { loadIndianMusicAlbumsPaginated } = useAlbumData();

  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const initialLoadTriggered = useRef(false);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore || !enabled) {
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await loadIndianMusicAlbumsPaginated(
        12,
        offsetRef.current,
        genre,
        search,
      );

      if (offsetRef.current === 0) {
        setAlbums(result?.items || []);
      } else {
        setAlbums((prev) => [...prev, ...(result?.items || [])]);
      }

      const hasNext = result?.hasNext || false;
      setHasMore(hasNext);
      offsetRef.current += result?.items?.length || 0;

      if (!initialLoadDone) {
        setInitialLoadDone(true);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load more items';
      setError(errorMessage);
      console.error('Error loading more items:', err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [
    loadIndianMusicAlbumsPaginated,
    hasMore,
    enabled,
    initialLoadDone,
    genre,
    search,
  ]);

  const reset = useCallback(() => {
    setAlbums([]);
    offsetRef.current = 0;
    setHasMore(true);
    setError(null);
    setLoading(false);
    loadingRef.current = false;
    setInitialLoadDone(false);
    initialLoadTriggered.current = false;
  }, []);

  useEffect(() => {
    setLoading(true);
    loadingRef.current = true;

    setAlbums([]);
    offsetRef.current = 0;
    setHasMore(true);
    setError(null);
    setInitialLoadDone(false);
    initialLoadTriggered.current = false;

    if (enabled) {
      const triggerLoad = async () => {
        try {
          const result = await loadIndianMusicAlbumsPaginated(
            12,
            0,
            genre,
            search,
          );

          setAlbums(result?.items || []);
          const hasNext = result?.hasNext || false;
          setHasMore(hasNext);
          offsetRef.current = (result?.items || []).length;
          setInitialLoadDone(true);
        } catch (err) {
          console.error('Error loading albums for genre/search:', err);
          setError(
            err instanceof Error ? err.message : 'Failed to load albums',
          );
          setHasMore(false);
        } finally {
          setLoading(false);
          loadingRef.current = false;
        }
      };

      triggerLoad();
    } else {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [genre, search, enabled, loadIndianMusicAlbumsPaginated]);

  useEffect(() => {
    if (!enabled || !hasMore || !sentinelRef.current) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const rootElement = scrollContainerRef?.current || null;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !loadingRef.current && hasMore) {
          loadMore();
        }
      },
      {
        root: rootElement,
        rootMargin,
        threshold: 0.1,
      },
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    enabled,
    hasMore,
    initialLoadDone,
    loadMore,
    rootMargin,
    scrollContainerRef,
  ]);

  useEffect(() => {
    if (!enabled || initialLoadTriggered.current) return;
    if (!initialLoadDone && albums.length === 0) {
      initialLoadTriggered.current = true;
      setLoading(true);
      loadingRef.current = true;
      loadMore();
    } else if (albums.length > 0 && !initialLoadDone) {
      initialLoadTriggered.current = true;
      setInitialLoadDone(true);
      offsetRef.current = albums.length;
    }
  }, [enabled, initialLoadDone, albums.length, loadMore]);

  return {
    loading,
    hasMore,
    error,
    loadMore,
    reset,
    sentinelRef,
    albums,
  };
};

export default useIndianMusicInfiniteScroll;
